import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Text,
  Icon,
  Box,
  Button,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { CalenderIcon } from "@root/assets/icons";
import { DownloadIcon } from "@root/assets/icons/downloadIcon";
import useNotice from "@root/hooks/useNotice";
import LinkifyText from "@root/components/common/LinkifyText";

type NoticeType = "alert" | "info" | "campaign";

export interface NoticeDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  notice: {
    id?: number;
    title?: string;
    description?: string;
    createdAt?: string;
    views?: number;
    type?: NoticeType;
    noticeType?: { name?: string };
    details?: string;
    documents?: Array<{
      id: number;
      signedUrl: string;
      originalName: string;
      fileName: string;
    }>;
  };
  formatDateToYYYYMMDD: (date: string) => string;
}

const NoticeDetailsModal: React.FC<NoticeDetailsModalProps> = ({
  isOpen,
  onClose,
  notice,
  formatDateToYYYYMMDD,
}) => {
  const { t } = useTranslation("common");
  const { downloadDocumentsZip, isDownloadingNotice } = useNotice();

  const handleDownloadFiles = async () => {
    if (!notice?.documents || notice.documents.length === 0) return;

    const documentIds = notice.documents.map((doc) => doc.id);
    const sanitizedTitle =
      notice.title?.replace(/[^a-z0-9]/gi, "_").toLowerCase() || "notice_files";

    await downloadDocumentsZip(
      documentIds,
      notice?.id,
      `${sanitizedTitle}.zip`
    );
  };

  const isDownloading = isDownloadingNotice(notice?.id);
  const hasDocuments = notice?.documents && notice.documents.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "sm", md: "xl" }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        bg="#fff"
        borderRadius={{ base: "16px", md: "16px" }}
        p={{ base: 3, md: 2 }}
        mx={{ base: 3, md: 4 }}
        my={{ base: "auto", md: "auto" }}
        maxH={{ base: "90dvh", md: "90vh" }}
        display="flex"
        flexDirection="column"
      >
        <ModalHeader
          fontWeight={600}
          mb="-8px"
          fontSize={{ base: "17px", md: "20px" }}
          color="#2F2F34"
          pr="40px"
        >
          {notice?.title}
        </ModalHeader>
        <ModalCloseButton
          color="#5C6670"
          boxSize="16px"
          aria-label={t("notice.modalCloseLabel")}
          mt={4}
          mr={4}
        />
        <ModalBody pb={6} overflowY="auto" flex="1">
          <Flex
            alignItems={{ base: "flex-start", sm: "center" }}
            justifyContent="space-between"
            flexDirection={{ base: "column", sm: "row" }}
            gap={{ base: 2, sm: 0 }}
            mb={4}
          >
            <Flex alignItems="center" gap={3} flexWrap="wrap">
              <Flex
                align="center"
                gap={1}
                color="#798692"
                fontSize={{ base: "13px", md: "15px" }}
              >
                <Icon as={CalenderIcon} boxSize={4} />
                <Text>{formatDateToYYYYMMDD(notice?.createdAt ?? "")}</Text>
              </Flex>
              <Text color="#798692" fontSize={{ base: "13px", md: "15px" }}>
                {notice?.noticeType?.name}
              </Text>
            </Flex>
            {hasDocuments && (
              <Button
                size="sm"
                variant="outline"
                borderColor="#1819B4"
                color="#1819B4"
                fontWeight={600}
                fontSize="12px"
                leftIcon={
                  isDownloading ? (
                    <Spinner size="xs" color="#1819B4" />
                  ) : (
                    <DownloadIcon boxSize={3} color="#1819B4" />
                  )
                }
                onClick={handleDownloadFiles}
                borderRadius="8px"
                height="32px"
                px="12px"
                py="4px"
                width={{ base: "100%", sm: "auto" }}
                _hover={{ bg: "#F5F7FA" }}
                isDisabled={isDownloading}
              >
                {isDownloading
                  ? t("notice.downloading")
                  : t("notice.downloadFiles")}
              </Button>
            )}
          </Flex>
          <Box
            color="#23262F"
            fontSize={{ base: "14px", md: "15px" }}
            fontWeight={400}
            whiteSpace="pre-wrap"
          >
            <LinkifyText text={notice?.details || notice?.description || ""} />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NoticeDetailsModal;
